<template>
    <common-content-spacer height="small">
        <common-buttons align="right">
            <common-button
                v-if="!isUser && !isEditAddressModalOpen"
                type="primary"
                :size="locationButtonSize"
                :isSubmittable="false"
                :isDisabled="isUser"
                :label="locationButtonLabel"
                @click="toggleIsCreatingLocation()"
            ></common-button>

            <common-button
                v-if="isEditAddressModalOpen"
                type="primary"
                :isSubmittable="false"
                :isDisabled="isUser"
                :label="$t('common.microcopy.back')"
                @click="isEditAddressModalOpen = false"
            ></common-button>
        </common-buttons>

        <common-page-split v-if="isCreatingLocation || isEditingLocation" position="center" spacing="0">
            <template #aside>
                <common-content-wrapper v-if="!isEditAddressModalOpen" width="tiny">
                    <common-content-spacer class="update-locations-component__address" height="medium">
                        <common-page-header
                            v-if="isCreatingLocation"
                            :heading="$t('components.locations.update_locations_component.enter_address')"
                            size="small"
                        ></common-page-header>
                        <common-buttons align="right">
                            <common-button
                                v-if="!showAutocomplete"
                                type="link"
                                :isSubmittable="false"
                                :label="$t('components.locations.update_locations_component.edit_address')"
                                @click="openEditAddressModal()"
                            ></common-button>
                        </common-buttons>

                        <GMapAutocomplete
                            v-if="showAutocomplete"
                            class="update-locations-component__autocomplete"
                            :placeholder="$t('components.locations.update_locations_component.enter_address_here')"
                            :selectFirstOnEnter="true"
                            @place_changed="setPlace"
                        ></GMapAutocomplete>
                        <add-location-form
                            v-else-if="isCreatingLocation"
                            :cityOptions="cityOptions"
                            :prefill="formData"
                            :isSubmittingForm="isSubmittingForm"
                            :isCompany="isCompany"
                            @search-again="searchAgain()"
                            @add-location="formSubmit"
                        ></add-location-form>
                        <edit-location-form
                            v-if="isEditingLocation"
                            :cityOptions="cityOptions"
                            :prefill="prefillEditForm"
                            :company="company"
                            :isSubmittingForm="isSubmittingForm"
                            @cancel-update="cancelUpdate()"
                            @emitEvent="emitEvent($event)"
                        ></edit-location-form>
                    </common-content-spacer>
                </common-content-wrapper>
                <common-content-wrapper v-if="isEditAddressModalOpen" width="tiny">
                    <edit-address-form @edit-address="setPlace" @close="isEditAddressModalOpen = false"></edit-address-form>
                </common-content-wrapper>
            </template>

            <template #main>
                <map-component
                    :markers="markers"
                    :mapCenter="mapCenter"
                    :isMarkerDraggable="true"
                    :defaultZoom="15"
                    @marker-drag="markerDrag"
                ></map-component>
            </template>
        </common-page-split>

        <div v-else>
            <div v-if="locations?.length">
                <div v-for="location in locations" :key="location.id" class="update-locations-component__all">
                    <div
                        v-if="!isUser"
                        type="button"
                        class="update-locations-component__item--dropdown"
                        @click.stop="toggleMenu(location.id)"
                    >
                        <common-icon name="three-horizontal-dots" size="large"></common-icon>
                        <common-dropdown
                            v-click-away="toggleMenu"
                            :isOpen="isDropdownOpen(location.id)"
                            :hasArrow="true"
                            arrowPositionY="top"
                            arrowPositionX="right"
                        >
                            <nav class="dropdown__menu">
                                <common-buttons class="update-locations-component__item--dropdown__buttons">
                                    <button type="button" @click="editLocation(location)">
                                        {{ $t('common.microcopy.edit') }}
                                    </button>
                                    <button type="button" @click="deleteLocation(location)">
                                        {{ $t('common.microcopy.delete') }}
                                    </button>
                                    <button
                                        v-if="location.locationType === LocationTypeEnum.BUSINESS"
                                        type="button"
                                        @click="createOffer(location)"
                                    >
                                        {{ $t('layouts.app.top_menu.create_service') }}
                                    </button>
                                </common-buttons>
                            </nav>
                        </common-dropdown>
                    </div>
                    <div class="update-locations-component__all--item update-locations-component__all--item--address">
                        <common-icon name="location" size="large"></common-icon>
                        <span @click="openMap(location.latitude, location.longitude)">{{ location.address }}</span>
                    </div>
                    <div class="update-locations-component__all--item update-locations-component__all--item--city">
                        <common-icon name="city" size="large"></common-icon>
                        {{ location.city?.name }} -
                        {{ $t(`forms.locations.add_location.${location.locationType.toLowerCase()}`) }}
                    </div>
                    <div
                        v-if="location.telephones[0].telephone"
                        class="update-locations-component__all--item update-locations-component__all--item--city"
                    >
                        <common-icon name="phone" size="large"></common-icon>
                        {{ location.telephones[0].telephone }}
                    </div>
                    <div
                        v-if="location.emails[0].email"
                        class="update-locations-component__all--item update-locations-component__all--item--email"
                    >
                        <common-icon name="email" size="large"></common-icon>
                        {{ location.emails[0].email }}
                    </div>
                </div>
            </div>
            <div v-else-if="!isCreatingLocation" class="update-locations-component__no-locations">
                <p>{{ $t('components.locations.update_locations_component.no_locations') }}</p>
            </div>
        </div>
        <common-yes-no-modal
            v-if="isYesNoModalOpen"
            :showWarningIcon="true"
            height="tiny"
            :headingText="redirectToUserModalHeading"
            :noButtonText="$t('common.microcopy.close')"
            :yesButtonText="$t('common.microcopy.go_to_profile')"
            @close="isYesNoModalOpen = false"
            @continue="goToPage()"
        ></common-yes-no-modal>
        <common-modal
            v-if="isLocationUsedModalOpen"
            :heading="$t('modals.location_used_modal.title')"
            @close="closeLocationUsedModal"
        >
            <common-page-plain>
                <common-content-wrapper width="medium">
                    <common-content-spacer height="medium">
                        <common-warning :message="$t('modals.location_used_modal.warning')"></common-warning>
                        <div>
                            <div v-for="offer in offersByLocation" :key="offer.id" class="out-of-availability__item">
                                <button @click="openOfferDetail(offer)">
                                    <b>{{ offer.titleTextContent.originalText }}</b>
                                </button>
                                <hr />
                            </div>
                        </div>
                        <common-buttons>
                            <common-button
                                type="secondary"
                                :label="$t('common.microcopy.close')"
                                @click="closeLocationUsedModal"
                            ></common-button>
                        </common-buttons>
                    </common-content-spacer>
                </common-content-wrapper>
            </common-page-plain>
        </common-modal>

        <common-yes-no-modal
            v-if="isDeleteLocationModalOpen"
            height="tiny"
            :showWarningIcon="true"
            :headingText="$t('modals.delete_location.title')"
            :noButtonText="$t('common.microcopy.false')"
            :yesButtonText="$t('common.microcopy.true')"
            @close="closeDeleteLocationModal"
            @continue="confirmDeleteLocation()"
        ></common-yes-no-modal>
    </common-content-spacer>
</template>

<script>
    import MapComponent from 'components/page-elements/locations/map/MapComponent'
    import AddLocationForm from 'components/forms/locations/AddLocationForm'
    import EditLocationForm from 'components/forms/locations/EditLocationForm'
    import Store from '@/store'
    import RoutesEnum from 'enums/routes.enum'
    import CountryEnum from 'enums/country.enum'
    import OfferAPI from 'api/OfferAPI'
    import LocationsAPI from 'api/LocationsAPI'
    import EditAddressForm from 'components/forms/locations/EditAddressForm'
    import LanguageEnum from 'enums/language.enum'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import LocationTypeEnum from 'enums/location_type.enum'
    import { cyrillicToLatin } from '~/helpers'
    export default {
        mixins: [WindowResizeMixin],
        components: { MapComponent, AddLocationForm, EditLocationForm, EditAddressForm },

        props: {
            locations: { type: Array, required: true },
            isSubmittingForm: { type: Boolean, default: false },
            isCompany: { type: Boolean, default: false },
            isUser: { type: Boolean, default: false },
            company: { type: Object, default: undefined },
        },

        data: () => ({
            //Belgrade coordinates
            mapCenter: { lat: 44.8125449, lng: 20.46123 },
            isCreatingLocation: false,
            isEditingLocation: false,
            markers: [],
            showAutocomplete: true,
            address: null,
            cityOptions: [],
            isYesNoModalOpen: false,
            isLocationUsedModalOpen: false,
            isDeleteLocationModalOpen: false,
            isEditAddressModalOpen: false,
            offersByLocation: [],
            selectedLocation: '',
            prefillEditForm: [],
            selectedDropdownId: null,
            selectedOfferId: null,
            LocationTypeEnum: LocationTypeEnum,
        }),

        computed: {
            locationButtonSize() {
                return this.$i18n.locale === LanguageEnum.RU && this.isSmartphone ? 'tiny' : 'tiny-micro'
            },
            locationButtonLabel() {
                return this.isCreatingLocation || this.isEditingLocation
                    ? this.$t('components.locations.update_locations_component.buttons.back')
                    : this.$t('components.locations.update_locations_component.buttons.create_new')
            },

            formData() {
                return {
                    coordinates: this.markers[0]?.coordinates,
                    address: this.address?.address,
                    city: this.address?.city,
                    country: this.address?.country,
                }
            },
            redirectToUserModalHeading() {
                return !this.$auth.areFirstAndLastNameCompleted()
                    ? this.$t('modals.complete_profile_modal.complete_first_and_last_name')
                    : this.$t('modals.complete_profile_modal.complete_mobile_phone')
            },
        },

        methods: {
            toggleIsCreatingLocation() {
                if (!this.$auth.isUserCompleteProfile()) {
                    this.isYesNoModalOpen = true
                    return
                }
                if (this.isEditingLocation) {
                    this.isEditingLocation = !this.isEditingLocation
                } else {
                    this.isCreatingLocation = !this.isCreatingLocation
                }
                this.searchAgain()
            },

            setPlace(place) {
                if (!place.address_components) {
                    return
                }
                this.isEditAddressModalOpen = false
                this.mapCenter = { lat: place?.geometry?.location?.lat(), lng: place?.geometry?.location?.lng() }
                this.markers = [{ coordinates: this.mapCenter }]
                this.showAutocomplete = false
                this.pullAddressFromAutocomplete(place.address_components)
            },

            openMap(long, lat) {
                window.open(`https://www.google.com/maps/search/?api=1&query=${long},${lat}`, '_blank')
            },

            redirectToUserProfile() {
                this.$router.push({ name: RoutesEnum.USER.ME, params: { tabType: 'profile' } })
                this.openProfileTab()
                this.isYesNoModalOpen = false
            },

            openProfileTab() {
                this.emitEvent({ name: 'open-profile-tab' })
                return
            },

            pullAddressFromAutocomplete(addressComponents) {
                let address = cyrillicToLatin(
                    addressComponents.find((addressComponent) => addressComponent.types.includes('route'))?.long_name,
                )
                const streetNumber = addressComponents.find((addressComponent) =>
                    addressComponent.types.includes('street_number'),
                )?.long_name
                const city = cyrillicToLatin(
                    addressComponents.find((addressComponent) => addressComponent.types.includes('locality'))?.long_name,
                )
                const country = cyrillicToLatin(
                    addressComponents.find((addressComponent) => addressComponent.types.includes('country'))?.long_name,
                )
                // TODO: Remove thes when we implement Bookeraj for all countries
                if (!this.isCompany) {
                    if (
                        country !== 'Serbia' &&
                        country !== 'Србија' &&
                        country !== 'Srbija' &&
                        country !== 'Bosnia and Herzegovina' &&
                        country !== 'Bosna i Hercegovina' &&
                        country !== 'Босна и Херцеговина'
                    ) {
                        this.showAutocomplete = true
                        return this.$toast.error(
                            this.$t('components.locations.update_locations_component.only_supported_country'),
                        )
                    }
                } else {
                    if (
                        this.company?.city?.country?.name === CountryEnum.BOSNIA &&
                        country !== 'Bosnia and Herzegovina' &&
                        country !== 'Bosna i Hercegovina' &&
                        country !== 'Босна и Херцеговина'
                    ) {
                        this.showAutocomplete = true
                        this.isEditAddressModalOpen = true
                        return this.$toast.error(
                            this.$t('components.locations.update_locations_component.company_bosnia_country'),
                        )
                    }

                    if (
                        this.company?.city?.country?.name === CountryEnum.SERBIA &&
                        country !== 'Serbia' &&
                        country !== 'Србија' &&
                        country !== 'Srbija'
                    ) {
                        this.showAutocomplete = true
                        this.isEditAddressModalOpen = true

                        return this.$toast.error(
                            this.$t('components.locations.update_locations_component.company_serbia_country'),
                        )
                    }
                }

                if (
                    country === 'Bosnia and Herzegovina' ||
                    country === 'Bosna i Hercegovina' ||
                    country === 'Босна и Херцеговина'
                ) {
                    this.fetchCities(CountryEnum.BOSNIA)
                }
                address = streetNumber ? `${address} ${streetNumber}` : address
                this.address = {
                    address,
                    city,
                    country,
                }

                if (this.isEditingLocation) {
                    this.setEditFormData()
                }
            },
            openOfferDetail(offer) {
                this.$router.push({ name: RoutesEnum.OFFER.SINGLE, params: { id: offer.id, offerType: 'details' } })
            },
            searchAgain() {
                this.showAutocomplete = true
            },

            markerDrag(data) {
                this.markers = [{ coordinates: data.newCoordinates }]
                this.prefillEditForm.coordinates = { lat: data.newCoordinates.lat, lng: data.newCoordinates.lng }
            },

            fetchCities(chosenCountry = CountryEnum.SERBIA) {
                this.cityOptions = Store.getters['data/getCountries']
                    .find((country) => country.name === chosenCountry)
                    .cities.map((city) => ({
                        value: city.id,
                        label: city.name,
                    }))
            },

            editLocation(location) {
                this.editLocationFormData(location)
                this.showAutocomplete = false
                this.isEditingLocation = !this.isEditingLocation
            },

            editLocationFormData(location) {
                this.mapCenter = { lat: location.latitude, lng: location.longitude }
                this.markers = [{ coordinates: this.mapCenter }]
                var prefillCountry = this.isCompany ? this.company?.city?.country?.name : location?.city?.country?.name
                this.fetchCities(prefillCountry)
                this.prefillEditForm = {
                    country: prefillCountry,
                    coordinates: { lat: location.latitude, lng: location.longitude },
                    address: location?.address,
                    city: location?.city?.name,
                    'telephones.0.telephone': location.telephones[0].telephone,
                    'emails.0.email': location?.emails[0]?.email,
                    locationId: location.id,
                    googleBusiness: location.googleBusiness,
                    locationType: location.locationType,
                }
            },

            setEditFormData() {
                this.prefillEditForm = {
                    ...this.prefillEditForm,
                    coordinates: this.mapCenter,
                    address: this.address?.address,
                    city: this.address?.city,
                    country: this.address?.country,
                }
            },

            createOffer(location) {
                if (this.isCompany) {
                    this.$router.push({
                        name: RoutesEnum.OFFER.CREATE,
                        query: { location: location.id, company: this.$route.params.id },
                    })
                } else {
                    this.$router.push({ name: RoutesEnum.OFFER.CREATE, query: { location: location.id } })
                }
            },

            cancelUpdate() {
                this.isEditingLocation = !this.isEditingLocation
            },

            deleteLocation(location) {
                this.selectedLocation = location
                if (this.isCompany) {
                    OfferAPI.getCompanyOffersByLocationId(location.id, this.$route.params.id).then((res) => {
                        this.isUsedLocation(res)
                    })
                } else {
                    OfferAPI.getUserOffersByLocationId(location.id).then((res) => {
                        this.isUsedLocation(res)
                    })
                }
            },

            isUsedLocation(response) {
                if (response.length) {
                    this.offersByLocation = response
                    this.showLocationUsedModal()
                } else {
                    this.showDeleteLocationModal()
                }
            },

            confirmDeleteLocation() {
                if (this.isCompany) {
                    LocationsAPI.deleteLocationForCompany(this.selectedLocation.id, this.$route.params.id)
                        .then(() => {
                            this.deleteLocationEvent()
                        })
                        .catch(() => {})
                } else {
                    LocationsAPI.deleteLocationForUser(this.selectedLocation.id)
                        .then(() => {
                            this.deleteLocationEvent()
                        })
                        .catch(() => {})
                }
                this.closeDeleteLocationModal()
            },

            deleteLocationEvent() {
                this.$toast.success(this.$t('components.locations.update_locations_component.deleted_success'))
                this.emitEvent({ name: 'location-deleted' })
            },

            showLocationUsedModal() {
                this.isLocationUsedModalOpen = true
            },
            closeLocationUsedModal() {
                this.isLocationUsedModalOpen = false
            },
            showDeleteLocationModal() {
                this.isDeleteLocationModalOpen = true
            },
            closeDeleteLocationModal() {
                this.isDeleteLocationModalOpen = false
            },

            formSubmit(location) {
                this.emitEvent({ name: 'add-location', payload: location })
            },
            isDropdownOpen(id) {
                return this.selectedDropdownId === id
            },
            toggleMenu(id) {
                this.selectedOfferId = id
                if (this.selectedDropdownId === id) {
                    return (this.selectedDropdownId = null)
                }
                this.selectedDropdownId = id
            },

            openEditAddressModal() {
                this.isEditAddressModalOpen = true
            },

            closeEditAddressModal() {
                this.isEditAddressModalOpen = false
            },
            goToPage() {
                !this.$auth.areFirstAndLastNameCompleted()
                    ? this.$router.push({ name: RoutesEnum.USER.ME, params: { tabType: 'profile' }, query: { tab: 'profile' } })
                    : this.$router.push({
                          name: RoutesEnum.USER.ME,
                          params: { tabType: 'personalDetails' },
                          query: { tab: 'personalDetails' },
                      })
            },
        },

        mounted() {
            this.fetchCities(this.company ? this.company?.city?.country?.name : undefined)
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/components/locations/_update-locations-component.scss';
</style>
